/**
 * Filesystem cache for {@link RemoteCatalogSource}.
 *
 * Cache layout (default `<cacheRoot>/catalog/`, see `defaultCatalogCacheDir`):
 *
 *   <dir>/
 *     index.json           — JSON map of metadata entries with TTL bookkeeping
 *     tarballs/<sha>.tgz   — content-addressed tarball blobs (SHA256-verified)
 *
 * Two caches live in `index.json`:
 *
 * - **resolve cache** — keyed by canonical ref (`<publisher>/<name>@<version>`),
 *   value is the {@link CatalogAsset} or `null` (negative cache for missing refs)
 *   plus a `fetchedAt` timestamp.
 * - **list cache** — keyed by a deterministic stringified filter,
 *   value is the array of {@link CatalogAsset} plus a `fetchedAt` timestamp.
 *
 * Tarball blobs are cached on disk by SHA256; the SHA256 itself acts as the
 * integrity tag, so no separate TTL is needed — content-addressed blobs never
 * become stale.
 *
 * Concurrency: writes go through atomic temp-file rename to keep readers consistent.
 * Multiple processes writing to the same cache dir is safe at the per-key level
 * (last writer wins) but readers may see momentarily stale data — acceptable
 * for a CLI cache.
 *
 * @docLink packages/library/api-reference#catalog-cache
 */

import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import type { CatalogAsset, CatalogAssetFilter } from "@skaile/workspaces/plugins";
import { globalCacheRoot, migrateLegacyDir } from "@skaile/workspaces/core";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * One entry in the resolve cache. `value` is `null` to negatively-cache misses.
 *
 * @docLink packages/library/api-reference#catalog-cache
 */
export interface ResolveCacheEntry {
  fetchedAt: number;
  value: CatalogAsset | null;
}

/**
 * One entry in the list cache. `key` is a stable string derived from the
 * filter; `items` is the response array.
 *
 * @docLink packages/library/api-reference#catalog-cache
 */
export interface ListCacheEntry {
  fetchedAt: number;
  items: CatalogAsset[];
}

/**
 * Persisted shape of `index.json`.
 *
 * @docLink packages/library/api-reference#catalog-cache
 */
export interface CatalogCacheIndex {
  /** Schema version — bumped on incompatible layout changes. */
  version: 1;
  /** Resolve cache by canonical ref. */
  resolve: Record<string, ResolveCacheEntry>;
  /** List cache by filter key. */
  list: Record<string, ListCacheEntry>;
}

/**
 * Options for {@link CatalogCache}.
 *
 * @docLink packages/library/api-reference#catalog-cache
 */
export interface CatalogCacheOptions {
  /** Cache directory. Default: `<cacheRoot>/catalog` (see `defaultCatalogCacheDir`). */
  dir?: string;
  /** TTL in milliseconds. `0` disables network and forces cache-only reads. */
  ttlMs: number;
  /** Override clock for tests. */
  now?: () => number;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * Catalog metadata cache dir: `<cacheRoot>/catalog`. Migrates the pre-consolidation
 * `~/.skaile/catalog-cache/`. NOTE: this now honours `SKAILE_CACHE_DIR`/`SKAILE_HOME`
 * (it previously keyed off `os.homedir()` directly) — see MIGRATION.md.
 */
export function defaultCatalogCacheDir(): string {
  const home = process.env.SKAILE_HOME ?? path.join(os.homedir(), ".skaile");
  return migrateLegacyDir(
    path.join(globalCacheRoot(), "catalog"),
    path.join(home, "catalog-cache"),
  );
}

const INDEX_FILE = "index.json";
const TARBALL_DIR = "tarballs";

// ---------------------------------------------------------------------------
// CatalogCache
// ---------------------------------------------------------------------------

/**
 * Filesystem-backed cache for catalog metadata + tarballs.
 *
 * Per-key freshness is computed against {@link CatalogCacheOptions.ttlMs}. When
 * `ttlMs === 0` the cache is treated as **air-gapped** — every entry is
 * considered valid forever, and the only way to refresh is an explicit
 * {@link CatalogCache.invalidateAll} call (driven by `skaile update`).
 *
 * @docLink packages/library/api-reference#catalog-cache
 */
export class CatalogCache {
  readonly dir: string;
  private readonly ttlMs: number;
  private readonly nowFn: () => number;
  private index: CatalogCacheIndex | null = null;

  constructor(opts: CatalogCacheOptions) {
    this.dir = opts.dir ?? defaultCatalogCacheDir();
    this.ttlMs = opts.ttlMs;
    this.nowFn = opts.now ?? Date.now;
    this.ensureDir();
  }

  // ── public: resolve cache ───────────────────────────────────────────────

  /**
   * Return a fresh resolve-cache entry for `ref`, or `null` if absent / stale.
   *
   * When `ttlMs === 0` (air-gapped), all entries are considered fresh
   * regardless of `fetchedAt`.
   */
  getResolve(ref: string): ResolveCacheEntry | null {
    const idx = this.loadIndex();
    const entry = idx.resolve[ref];
    if (!entry) return null;
    if (this.ttlMs === 0) return entry;
    if (this.nowFn() - entry.fetchedAt > this.ttlMs) return null;
    return entry;
  }

  /**
   * Return a stale resolve-cache entry regardless of TTL — for stale-while-revalidate.
   */
  getResolveStale(ref: string): ResolveCacheEntry | null {
    const idx = this.loadIndex();
    return idx.resolve[ref] ?? null;
  }

  /** Persist a resolve-cache entry (use `null` to negatively cache misses). */
  setResolve(ref: string, value: CatalogAsset | null): void {
    const idx = this.loadIndex();
    idx.resolve[ref] = { fetchedAt: this.nowFn(), value };
    this.writeIndex(idx);
  }

  // ── public: list cache ──────────────────────────────────────────────────

  /**
   * Return a fresh list-cache entry for `filter`, or `null` if absent / stale.
   */
  getList(filter?: CatalogAssetFilter): ListCacheEntry | null {
    const key = filterKey(filter);
    const idx = this.loadIndex();
    const entry = idx.list[key];
    if (!entry) return null;
    if (this.ttlMs === 0) return entry;
    if (this.nowFn() - entry.fetchedAt > this.ttlMs) return null;
    return entry;
  }

  /** Return a stale list-cache entry regardless of TTL — for stale-while-revalidate. */
  getListStale(filter?: CatalogAssetFilter): ListCacheEntry | null {
    const key = filterKey(filter);
    const idx = this.loadIndex();
    return idx.list[key] ?? null;
  }

  /** Persist a list-cache entry. */
  setList(filter: CatalogAssetFilter | undefined, items: CatalogAsset[]): void {
    const key = filterKey(filter);
    const idx = this.loadIndex();
    idx.list[key] = { fetchedAt: this.nowFn(), items };
    this.writeIndex(idx);
  }

  // ── public: tarball cache ───────────────────────────────────────────────

  /** Absolute path where a tarball with `sha256` would be cached. */
  tarballPath(sha256: string): string {
    return path.join(this.dir, TARBALL_DIR, `${sha256}.tgz`);
  }

  /** Read a cached tarball from disk if present, otherwise `null`. */
  readTarball(sha256: string): Uint8Array | null {
    const p = this.tarballPath(sha256);
    if (!fs.existsSync(p)) return null;
    return fs.readFileSync(p);
  }

  /** Persist a tarball blob to disk (atomic write). */
  writeTarball(sha256: string, bytes: Uint8Array): void {
    const dir = path.join(this.dir, TARBALL_DIR);
    if (!fs.existsSync(dir)) fs.mkdirSync(dir, { recursive: true });
    const finalPath = this.tarballPath(sha256);
    const tmpPath = `${finalPath}.${process.pid}.${Math.random().toString(36).slice(2)}.tmp`;
    fs.writeFileSync(tmpPath, bytes);
    fs.renameSync(tmpPath, finalPath);
  }

  // ── public: invalidation ────────────────────────────────────────────────

  /**
   * Wipe all cached metadata (resolve + list). Tarballs are content-addressed
   * and remain valid — they are not invalidated.
   *
   * Called by `skaile update`.
   */
  invalidateAll(): void {
    const idx: CatalogCacheIndex = { version: 1, resolve: {}, list: {} };
    this.index = idx;
    this.writeIndex(idx);
  }

  /**
   * Wipe all cached metadata **and** delete tarball blobs. Used for full reset.
   */
  invalidateEverything(): void {
    this.invalidateAll();
    const tarballDir = path.join(this.dir, TARBALL_DIR);
    if (fs.existsSync(tarballDir)) {
      fs.rmSync(tarballDir, { recursive: true, force: true });
    }
  }

  /** Return whether `ttlMs` is zero (air-gapped mode). */
  get airGapped(): boolean {
    return this.ttlMs === 0;
  }

  // ── private ─────────────────────────────────────────────────────────────

  private ensureDir(): void {
    if (!fs.existsSync(this.dir)) fs.mkdirSync(this.dir, { recursive: true });
    const tarballDir = path.join(this.dir, TARBALL_DIR);
    if (!fs.existsSync(tarballDir)) fs.mkdirSync(tarballDir, { recursive: true });
  }

  private indexPath(): string {
    return path.join(this.dir, INDEX_FILE);
  }

  private loadIndex(): CatalogCacheIndex {
    if (this.index) return this.index;
    const p = this.indexPath();
    if (!fs.existsSync(p)) {
      this.index = { version: 1, resolve: {}, list: {} };
      return this.index;
    }
    try {
      const raw = JSON.parse(fs.readFileSync(p, "utf-8"));
      if (raw && typeof raw === "object" && raw.version === 1) {
        this.index = {
          version: 1,
          resolve: raw.resolve ?? {},
          list: raw.list ?? {},
        };
        return this.index;
      }
    } catch {
      // fall through to fresh
    }
    this.index = { version: 1, resolve: {}, list: {} };
    return this.index;
  }

  private writeIndex(idx: CatalogCacheIndex): void {
    this.index = idx;
    const p = this.indexPath();
    const tmp = `${p}.${process.pid}.${Math.random().toString(36).slice(2)}.tmp`;
    fs.writeFileSync(tmp, JSON.stringify(idx, null, 2), "utf-8");
    fs.renameSync(tmp, p);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Stable string key for a {@link CatalogAssetFilter}. Sorts keys so semantically
 * equivalent filters share a cache slot.
 */
export function filterKey(filter?: CatalogAssetFilter): string {
  if (!filter) return "__all__";
  const keys = Object.keys(filter).sort();
  const norm: Record<string, unknown> = {};
  for (const k of keys) {
    const v = (filter as Record<string, unknown>)[k];
    if (v !== undefined) norm[k] = v;
  }
  return Object.keys(norm).length === 0 ? "__all__" : JSON.stringify(norm);
}

/**
 * Compute the SHA-256 digest of `bytes` as a lowercase hex string.
 *
 * Used by {@link RemoteCatalogSource.fetchTarball} to verify integrity of
 * downloaded tarballs (spec invariant: "Hash pinning ... Mismatch = hard fail").
 *
 * @docLink packages/library/api-reference#catalog-cache
 */
export function sha256Hex(bytes: Uint8Array): string {
  const h = crypto.createHash("sha256");
  h.update(bytes);
  return h.digest("hex");
}
